import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { StyledHeader } from "./ClubFinancial";
import LabelText from "../../../ui/LabelText";
import { useTheme } from "../../../contexts/ThemeContext";
import { IClubFinancialData, IseasonData, ISquadPlayers, IsquadStructureWagesPW } from "../../../types/IClubDetail";
import ToggleButton from "../../../ui/ToggleButton/ToggleButton";
import styled from "styled-components";
import LargeModal from "../../../ui/LargeModal";
import { ToolTipStyles } from "../../../styled-components/Tooltip.Styles";
import ReactDOMServer from "react-dom/server";
import graphIcon from "../../../assets/icons/graphIcon.png";
import { useHorizontalScroll } from "../../../utils/HorizontalScrollHook";
import SecondaryButton from "../../../ui/SecondaryButton";
import CustomDropdown from "../../../ui/CustomDropdown";
import IApiResponse from "../../../types/IApiResponse";
import { fetchData, postData } from "../../../services/api";
import apiUrls from "../../../config/apiUrls";
import handleApiResponse from "../../../utils/handleApiResponse";
import { ModuleSpinner } from "../../../ui/LoadingSpinner/ModuleSpinner";

const SquareChartContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.playerDataBorder};
`;

interface ISquadStrcutureData {
  corePlayers: number[] | undefined;
  regularStarter: number[] | undefined;
  squadPlayers: number[] | undefined;
  fringePlayers: number[] | undefined;
  keyPlayers: number[] | undefined;
  wages: { [key: string]: { playerIds: number[]; leagueAverageCount: number | null } };
}

interface ClubSquadStructureProps {
  teamId: number;
  simulationSeasonId?: number
  isForSimulation?: boolean
}

const SquareBarChart: React.FC<ClubSquadStructureProps> = ({ teamId , simulationSeasonId , isForSimulation}) => {
  const [playerWagesData, setPlayerWagesData] = useState<IClubFinancialData | undefined>();
  const [selectedLabel, setSelectedLabel] = useState<string>("1");
  const [seasonId, setSeasonId] = useState<number>();
  const [seasonData, setSeasonData] = useState<any[]>([]);
  const [seasonLabel, setSeasonLabel] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFullGraph, setShowFullGraph] = useState<boolean>(false);

  useEffect(() => {
    const fetchSeasonData = async () => {
      const response: IApiResponse<IseasonData> = await fetchData(`${apiUrls["GetAllCompetitionSeasonsByTeamId"]}?TeamId=${teamId}`);
      handleApiResponse(
        response,
        (teamsData) => {
          setSeasonData(teamsData.seasonData);
          setSeasonId(teamsData.previousSeasonId);
          const matchedSeason = teamsData.seasonData.find((season) => season.value === teamsData.previousSeasonId);
          if (matchedSeason) {
            setSeasonLabel(matchedSeason.label);
          }
        },
        (error) => {
          console.error("Error fetching squad chart Data:", error);
        }
      );
    };
    fetchSeasonData();
  }, [teamId]);

  useEffect(() => {
    const fetchFinancialData = async () => {
      if (seasonId! > 0) {
        try {
          setIsLoading(true);
          const requestParameters = {
            TeamId: teamId,
            seasonId: simulationSeasonId! > 0 ? simulationSeasonId! : seasonId,
          };
          const [playerWagesResponse] = await Promise.all([
            postData(apiUrls["GetTeamFinancialPlayerWagesData"], requestParameters) as Promise<IApiResponse<IClubFinancialData>>,
          ]);
          setPlayerWagesData(playerWagesResponse.data);
        } catch (error) {
          console.error("Error fetching financial data:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchFinancialData();
  }, [teamId, seasonId , simulationSeasonId!]);

  // Function to dynamically bind squad structure wages data to wages
  const getWagesStructure = (squadStructureWagesPW: IsquadStructureWagesPW[] | undefined) => {
    const wages: any = {};
    squadStructureWagesPW?.forEach((wageInfo) => {
      const { maxValueText, playerIds, leagueAverageCount } = wageInfo;
      const wageKey = `${maxValueText}`; // Create key as maxValueK, e.g., "10K", "20K", etc.
      wages[wageKey] = {
        playerIds,
        leagueAverageCount,
      };
    });
    return wages;
  };
  const squadPlayersInfo = playerWagesData?.playerWages.squadPlayers;
  //CORE PLAYERS
  const corePlayers = squadPlayersInfo?.filter((player) => player.isCorePlayer).map((player) => player.playerId);
  //REGULAR STARTTER
  const regularStarter = squadPlayersInfo?.filter((player) => player.isRegularStarterPlayer).map((player) => player.playerId);
  //SQUAD PLAYERS
  const squadPlayers = squadPlayersInfo?.filter((player) => player.isSquadPlayer).map((player) => player.playerId);
  //FRINGE PLAYERS
  const fringePlayers = squadPlayersInfo
    ?.filter((player) => !player.isCorePlayer && !player.isRegularStarterPlayer && !player.isSquadPlayer)
    .map((player) => player.playerId);
  //KEY PLAYERS
  const keyPlayers = squadPlayersInfo?.filter((player) => player.isKeyPlayer).map((player) => player.playerId);
  //WAGES
  const wages =
    selectedLabel === "1"
      ? getWagesStructure(playerWagesData?.playerWages.squadStructureAge)        
      : selectedLabel === "3"
      ? getWagesStructure(playerWagesData?.playerWages.squadStructureWagesPW)
      : getWagesStructure(playerWagesData?.playerWages.squadStructureContractLeft);
  const data = { corePlayers, regularStarter, squadPlayers, fringePlayers, keyPlayers, wages } as ISquadStrcutureData;
  const squadPlayersData: ISquadPlayers[] | undefined = playerWagesData?.playerWages.squadPlayers;
  const toggleLabel = [
    { key: "1", value: "Age" },
    { key: "2", value: "Contract Remaining" },
    { key: "3", value: "Wages pw" },
  ];
  const handleToggleChange = (SelectedTab: any) => {
    setSelectedLabel(SelectedTab.key);
  };
  const handleSeasonChange = (seasonId: string) => {
    setSeasonId(Number(seasonId));
    const matchedSeason = seasonData.find((season) => season.value === seasonId);
    if (matchedSeason) {
      setSeasonLabel(matchedSeason.label);
    }
  };

  const { theme } = useTheme();
  const scrollRef = useHorizontalScroll();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [svgHeight, setSvgHeight] = useState(215);
  // const barWidth = Object.keys(data.wages).length > 10 ? 90 : scrollRef.current?.clientWidth! / Object.keys(data.wages).length;
  const barWidth = Object.keys(data.wages).length > 10 
  ? 90 
  : (Object.keys(data.wages).length > 0 
    ? scrollRef.current?.clientWidth! / Object.keys(data.wages).length 
    : 80); 
  const colors = [theme.playerCardBackground, theme.playerContentBackground];
  const barHeight = 24;
  const [isLargeModalOpen, setLargeModalIsOpen] = useState(false);
  const totalBarGroupWidth = data !== null ? Object.keys(data.wages).length * barWidth : barWidth;
  // const adjustedChartWidth = Math.max(1074, totalBarGroupWidth);
  const adjustedChartWidth = Math.max(1315, totalBarGroupWidth);


  const toggleLargeModal = () => {
    setLargeModalIsOpen(!isLargeModalOpen);
  };

  const modalData = {
    headerTitle: "Data not provided by club",
    actionTag: " ",
    items: [
      {
        subTitle: " ",
        description:
          "Leeds United are situated in a country who’s laws to not require them to make public the data we’d need to fully calculate this sum.",
      },
    ],
  };

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setIsLeftDisabled(scrollLeft === 0);
      setIsRightDisabled(scrollLeft + clientWidth >= scrollWidth);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -600, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 600, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener("scroll", checkScrollButtons);
      checkScrollButtons(); // Initial check
    }
    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener("scroll", checkScrollButtons);
      }
    };
  }, [playerWagesData, selectedLabel]);

  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.on("wheel", (event) => {
      event.stopPropagation();
    });
    const formatDateWithSuffix = (date: any) => {
      const day = date.getDate();
      const suffix = (day: any) => {
        if (day > 3 && day < 21) return "th";
        switch (day % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };
      const month = date.toLocaleDateString("en-GB", { month: "short" });
      const year = date.getFullYear();
      return `${day}${suffix(day)} ${month} ${year}`;
    };
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "fixed")
      .style("z-index", 999)
      .style("visibility", "hidden")
      .style("background-color", theme.secondaryButtonBackground)
      .style("color", theme.color)
      .style("border", "1px solid ${theme.secondaryButtonBackground}")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)");
    const generateTooltipContent = (
      playerName: string | undefined,
      wagesText: string | undefined,
      percentage: number | undefined,
      contractLeft: string | undefined,
      age: number | undefined,
      ageText: string | undefined,
      dob: string | undefined,
      GK: string | undefined
    ) => {
      return ReactDOMServer.renderToStaticMarkup(
        <div>
          <LabelText className="squad-chart-tooltip">{playerName}</LabelText> •
          <LabelText className="squad-chart-tooltip"> {selectedLabel === "3" && wagesText !== null ? `€${wagesText}  ` : ""}</LabelText>
          <LabelText className="total-new">{selectedLabel === "3" ? `  (${percentage}% of Total)` : ""}</LabelText>
          <LabelText>{selectedLabel === "2" && contractLeft !== null ? `${contractLeft}  left on contract ` : ""}</LabelText>
          <LabelText>{selectedLabel === "1" ? `${ageText}` : ""}</LabelText>
          {selectedLabel === "1" && "•"}
          <LabelText> {selectedLabel === "1" ? `DOB : ${formatDateWithSuffix(new Date(dob!))}` : ""}</LabelText>
        </div>
      );
    };
    const maxPlayersInGroup = Math.max(...Object.values(data.wages).map((group) => (group.playerIds ? group.playerIds.length : 0)));
    const maxLineNumber = Math.max(...Object.values(data.wages).map((group) => group.leagueAverageCount || 0));
    const max = playerWagesData?.playerWages.squadStructureWagesPW === null ? 5  : maxPlayersInGroup > maxLineNumber ? maxPlayersInGroup : maxLineNumber;
    const yHeight = max === 0 ? max + 5 : max;
    const margin = { top: 20, right: -30, bottom: 40, left: -50 };
    const width = adjustedChartWidth - margin.left - margin.right;
    const customHeight = yHeight * (barHeight + 5) + margin.top + margin.bottom;
    const height = showFullGraph || playerWagesData?.playerWages.showGraphExpandButton === false ? customHeight : 215;
    setSvgHeight(height); // Update SVG height
    const xScale = d3.scaleBand().domain(Object.keys(data.wages)).range([0, width]).padding(0.85);
    const yScale = d3
      .scaleLinear()
      .domain([0, showFullGraph && playerWagesData?.playerWages.showGraphExpandButton ? yHeight : 5.1])
      .range([height - margin.top - margin.bottom, 0]);
    svg.selectAll("*").remove();
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
    const xAxis = d3.axisBottom(xScale).tickSize(0).tickPadding(24);
    g.append("line")
      .attr("x1", -80)
      .attr("y1", height - 58)
      .attr("x2", width + 50)
      .attr("y2", height - 58)
      .style("stroke", theme.playerDataBorder)
      .style("stroke-width", 2);
    const xAxisGroup = g
      .append("g")
      .attr("transform", `translate(0,${height - 60})`)
      .call(xAxis)
      .style("font-size", "14px")
      .style("font-family", "DM Mono")
      .style("font-style", "normal")
      .style("font-weight", 500);
    xAxisGroup.select(".domain").remove();
    g.append("defs")
      .append("pattern")
      .attr("id", "diagonalHatch")
      .attr("patternUnits", "userSpaceOnUse")
      .attr("width", 8) // Increased width
      .attr("height", 8) // Increased height
      .append("path")
      .attr("d", "M-1,1 l4,-4 M0,8 l8,-8 M7,9 l4,-4") // Adjusted coordinates
      .attr("stroke", "#00D1A8")
      .attr("stroke-width", 2);
    g.append("g").call(d3.axisLeft(yScale).ticks(yHeight));
    const playerPositions: Record<number, { x: number; y: number }> = {};
    const stackedData = Object.entries(data.wages).map(([range, { playerIds, leagueAverageCount }]) => {
      const corePlayersList = playerIds ? playerIds.filter((p) => data.corePlayers?.includes(p)) : [];
      const regularStarterList = playerIds ? playerIds.filter((p) => data.regularStarter?.includes(p)) : [];
      const squadPlayersList = playerIds ? playerIds.filter((p) => data.squadPlayers?.includes(p)) : [];
      const fringePlayersList = playerIds ? playerIds.filter((p) => data.fringePlayers?.includes(p)) : [];
      return { range, corePlayersList, regularStarterList, squadPlayersList, fringePlayersList, leagueAverageCount };
    });
    const lineGenerator = d3
      .line<{ x: number; y: number }>()
      .x((d) => d.x)
      .y((d) => d.y)
      .curve(d3.curveBasis);
       stackedData.forEach((obj) => {
      if (obj.leagueAverageCount == null) {
        obj.leagueAverageCount = 0;
      }
    });
    const lineData = stackedData.reduce<{ x: number; y: number }[]>((acc, curr, i) => {
      if (i === 0) {
        acc.push({ x: xScale(curr.range)! + xScale.bandwidth() / 2, y: yScale(curr.leagueAverageCount!) });
      } else {
        const prev = stackedData[i - 1];
        if (prev.leagueAverageCount !== null) {
          acc.push({ x: xScale(prev.range)! + xScale.bandwidth() / 2, y: yScale(prev.leagueAverageCount) });
        }
        if (curr.leagueAverageCount !== null) {
          acc.push({ x: xScale(curr.range)! + xScale.bandwidth() / 2, y: yScale(curr.leagueAverageCount) });
        }
      }
      return acc;
    }, []);
    const revisedStackedData = stackedData.length ? stackedData : Array(15).fill({ range: 'No Data' });
    
  revisedStackedData.forEach((d, i) => {
    g.append("rect")
      .attr("width", barWidth)
      .attr("height", height - 20)
      .attr("x", (i * barWidth)+ ((xScale.bandwidth() - barWidth) / 2) - 10)
      .attr("y", -margin.bottom)
      .style("fill", colors[i % 2]);
  });
    g.append("path")
      .datum(lineData)
      .attr("fill", "none")
      .attr("stroke", "#00A1E4")
      .attr("stroke-width", 2)
      .attr(
        "d",
        d3
          .line<any>()
          .x((d) => d.x)
          .y((d) => d.y)
          .curve(d3.curveMonotoneX) // Apply smooth curve
      );
    let tooltipRef: NodeJS.Timeout;
    stackedData.forEach((d, i) => {
      let currentY = height - margin.top - 5 - margin.bottom;
      const renderBar = (playerId: number, fill: string, stroke: string) => {
        currentY -= barHeight + 5;
        const squadPlayer = squadPlayersData?.find((p) => p.playerId === playerId);
        const bar = g
          .append("rect")
          .attr("x", xScale(d.range)! + (xScale.bandwidth() - 20) / 2)
          .attr("y", currentY + 7)
          .attr("width", 24)
          .attr("height", 24)
          .attr("fill", fill)
          .attr("stroke", stroke)
          .attr("stroke-width", 2)
          .attr("rx", 2)
          .attr("ry", 2)
          .text(squadPlayer?.isGoalKeeper ? "GK" : "")
          .style("cursor", "pointer")
          .on("mouseover", (event) => {
            tooltipRef = setTimeout(() => {
              tooltip
                .style("visibility", "visible")
                .style("left", `${event.clientX - 50}px`)
                .style("top", `${event.clientY - 50}px`)
                .html(
                  generateTooltipContent(
                    squadPlayer?.playerName,
                    squadPlayer?.wagePWText,
                    squadPlayer?.percentageOfTotalWage,
                    squadPlayer?.contractLeft,
                    squadPlayer?.age,
                    squadPlayer?.ageText,
                    squadPlayer?.dob,
                    squadPlayer?.isGoalKeeper ? "GK" : ""
                  )
                );
            }, 0);
          })
          .on("mousemove", (event) => {
            tooltip.style("left", `${event.clientX - 50}px`).style("top", `${event.clientY - 50}px`);
          })
          .on("mouseout", () => {
            clearTimeout(tooltipRef);
            tooltip.style("visibility", "hidden");
          });
        if (squadPlayer?.isSquadPlayer && squadPlayer.isGoalKeeper) {
          g.append("rect")
            .attr("x", xScale(d.range)! + (xScale.bandwidth() - 17) / 4)
            .attr("y", currentY + 13)
            .attr("width", 18)
            .attr("height", 14)
            .attr("fill", theme.thumbBackground)
            .attr("stroke-width", 2)
            .attr("rx", 2)
            .attr("ry", 2)
            .text(squadPlayer?.isGoalKeeper ? "GK" : "")
            .style("cursor", "pointer")
            .on("mouseover", (event) => {
              tooltipRef = setTimeout(() => {
                tooltip
                  .style("visibility", "visible")
                  .style("left", `${event.clientX - 50}px`)
                  .style("top", `${event.clientY - 50}px`)
                  .html(
                    generateTooltipContent(
                      squadPlayer?.playerName,
                      squadPlayer?.wagePWText,
                      squadPlayer?.percentageOfTotalWage,
                      squadPlayer?.contractLeft,
                      squadPlayer?.age,
                      squadPlayer?.ageText,
                      squadPlayer?.dob,
                      squadPlayer?.isGoalKeeper ? "GK" : ""
                    )
                  );
              }, 500);
            })
            .on("mousemove", (event) => {
              tooltip.style("left", `${event.clientX - 50}px`).style("top", `${event.clientY - 50}px`);
            })
            .on("mouseout", () => {
              clearTimeout(tooltipRef);
              tooltip.style("visibility", "hidden");
            });
        }
        function getTextColor(squadPlayer: ISquadPlayers | undefined): string {
          if (squadPlayer?.isSquadPlayer) {
            return "#00BE99";
          } else if (squadPlayer?.isRegularStarterPlayer || squadPlayer?.isCorePlayer) {
            return theme.thumbBackground;
          } else {
            return "#00BE99"; // Ensure this always returns a valid string
          }
        }
        g.append("text")
          .attr("x", xScale(d.range)! + (xScale.bandwidth() + 5) / 2)
          .attr("y", currentY + 25)
          .attr("text-anchor", "middle") // Center the text
          .style("font-family", "Aeonik") // Set font family to Aeonik
          .style("font-size", "12px") // Set font size
          .style("font-weight", 500)
          .style("cursor", "pointer")
          .style("fill", getTextColor(squadPlayer)) // Set text color using the function
          .text(squadPlayer?.isGoalKeeper ? "GK" : "")
          .style("cursor", "pointer")
          .on("mouseover", (event) => {
            tooltipRef = setTimeout(() => {
              tooltip
                .style("visibility", "visible")
                .style("left", `${event.clientX - 50}px`)
                .style("top", `${event.clientY - 50}px`)
                .html(
                  generateTooltipContent(
                    squadPlayer?.playerName,
                    squadPlayer?.wagePWText,
                    squadPlayer?.percentageOfTotalWage,
                    squadPlayer?.contractLeft,
                    squadPlayer?.age,
                    squadPlayer?.ageText,
                    squadPlayer?.dob,
                    squadPlayer?.isGoalKeeper ? "GK" : ""
                  )
                );
            }, 500);
          })
          .on("mousemove", (event) => {
            tooltip.style("left", `${event.clientX - 50}px`).style("top", `${event.clientY - 50}px`);
          })
          .on("mouseout", () => {
            clearTimeout(tooltipRef);
            tooltip.style("visibility", "hidden");
          });
        playerPositions[playerId] = {
          x: parseFloat(bar.attr("x")) + parseFloat(bar.attr("width")) / 2,
          y: parseFloat(bar.attr("y")) + parseFloat(bar.attr("height")) / 2,
        };
      };
      d.corePlayersList.forEach((player) => renderBar(player, "#FF7800", "#FF7800"));
      d.regularStarterList.forEach((player) => renderBar(player, "#00D1A8", "#00D1A8"));
      d.squadPlayersList.forEach((player) => renderBar(player, "url(#diagonalHatch)", "#00D1A8"));
      d.fringePlayersList.forEach((player) => renderBar(player, "transparent", "#00D1A8"));
    });
    if (max === 0) {
      svg
        .append("rect")
        .attr("class", "rect")
        .attr("x", width / 2 - 140)
        .attr("y", height / 2 + -40)
        .attr("text-anchor", "middle")
        .attr("width", 190)
        .attr("height", 35)
        .style("stroke", theme.emptyDataBorder)
        .style("cursor", "pointer")
        .style("fill", theme.playerDataBorder)
        .on("click", (event, d) => {
          setLargeModalIsOpen(!isLargeModalOpen);
        });
      const displayText = "Data not provided by club";
      svg
        .append("text")
        .attr("x", width / 2 + -50)
        .attr("y", height / 2 + -20)
        .attr("text-anchor", "middle") // Center the text
        .style("font-family", "Aeonik") // Set font family to Aeonik
        .style("font-size", "12px") // Set font size
        .style("font-weight", 500)
        .style("cursor", "pointer")
        .style("fill", theme.playerColor) // Set text color
        .text(displayText)
        .on("click", (event, d) => {
          setLargeModalIsOpen(!isLargeModalOpen);
        });
      const iconX = width / 2 + 25; // Adjust the X position as needed
      const iconY = height / 2 + -32; // Adjust the Y position as needed
      svg
        .append("path")
        .attr(
          "d",
          "M 11 18 h 2 v -2 h -2 Z m 1 -16 C 6.48 2 2 6.48 2 12 s 4.48 10 10 10 s 10 -4.48 10 -10 S 17.52 2 12 2 m 0 18 c -4.41 0 -8 -3.59 -8 -8 s 3.59 -8 8 -8 s 8 3.59 8 8 s -3.59 8 -8 8 m 0 -14 c -2.21 0 -4 1.79 -4 4 h 2 c 0 -1.1 0.9 -2 2 -2 s 2 0.9 2 2 c 0 2 -3 1.75 -3 5 h 2 c 0 -2.25 3 -2.5 3 -5 c 0 -2.21 -1.79 -4 -4 -4"
        )
        .attr("transform", `translate(${iconX}, ${iconY})`)
        .attr("transform", `translate(${iconX}, ${iconY}) scale(0.7)`) // Scale down by 50%
        .style("fill", theme.playerColor)
        .on("click", (event, d) => {
          setLargeModalIsOpen(!isLargeModalOpen); // Example alert, you can use any property from the data
        });
    }
  }, [showFullGraph, data, theme, squadPlayersData]);

  return (
    <>
      <LargeModal toggleLargeModal={toggleLargeModal} isOpen={isLargeModalOpen} data={modalData} />
      <div className="header-content-wrapper common-squad-structure-content">
        <StyledHeader className="header-content">
          <div className="chart-bottom-container">
            <LabelText className="player-family-name">Squad structure</LabelText>
            <div className="squad-toggle-container">
              {!isForSimulation &&(
                <div className="custom-dropdown">
                  <CustomDropdown placeholder="SEASON" isDisplaySearchInput={false} options={seasonData} onChange={handleSeasonChange} deafaultSelected={seasonLabel} Id={Number(teamId)}/>
                </div>
              )}
              
              <ToggleButton labels={toggleLabel || []} defaultLabelKey={selectedLabel!} onChange={handleToggleChange} />
            </div>
          </div>
        </StyledHeader>
        {playerWagesData?.playerWages.showGraphExpandButton && (
            <SecondaryButton
            label={showFullGraph ? "Hide full graph" : "Show full graph"}
            iconType="expander"
            onClick={() => setShowFullGraph(!showFullGraph)}
            className="btn-align"
          />
         )} 
        
        <div className="square-chart-main-container">
          <SquareChartContainer className="square-chart-container">
            <div ref={scrollRef} className="square-chart-inner-container">
              <div className="square-btn-container">
                <SecondaryButton className="arrow-left" iconType="keyboardarrowleft" onClick={scrollLeft} disabled={isLeftDisabled} />
              </div>
              <div className="custom-height-new">
                <div ref={scrollContainerRef} className={showFullGraph ? "square-svg-container" : "square-svg-container"}>
                {(playerWagesData?.playerWages?.squadStructureWagesPW === null && selectedLabel === '3') && (
                  <div className="no-squarechart-data">
                    <div className="no-data-content">
                      <LabelText className="no-data-text">Data not currently available</LabelText>
                    </div>
                  </div>
                )}
                {(playerWagesData?.playerWages?.squadStructureContractLeft === null && selectedLabel === '2') && (
                  <div className="no-squarechart-data">
                    <div className="no-data-content">
                      <LabelText className="no-data-text">Data not currently available</LabelText>
                    </div>
                  </div>
                )}
                {(playerWagesData?.playerWages?.squadStructureAge === null && selectedLabel === '1') && (
                  <div className="no-squarechart-data">
                    <div className="no-data-content">
                      <LabelText className="no-data-text">Data not currently available</LabelText>
                    </div>
                  </div>
                )}

                  
                  <svg ref={svgRef} width={adjustedChartWidth} height={svgHeight}></svg>
                </div>
              </div>
              <div className="square-btn-container">
                <SecondaryButton className="arrow-right" iconType="keyboardarrowright" onClick={scrollRight} disabled={isRightDisabled} />
              </div>
              {isLoading && <ModuleSpinner />}
            </div>
            <ToolTipStyles ref={tooltipRef} className="tooltip-wrapper tooltip" />
            <div className="marker-content">
              <div className="marker-container">
                <div className="square-marker" style={{ backgroundColor: "#FF7800", border: "1px solid #FF7800" }}></div>
                <LabelText className="selected-zone-name">Core player</LabelText>
              </div>
              <div className="marker-container">
                <div className="square-marker" style={{ backgroundColor: "#00D1A8" }}></div>
                <LabelText className="selected-zone-name">Regular starter</LabelText>
              </div>
              <div className="marker-container">
                <img src={graphIcon} className="square-marker" alt="graphIcon" style={{ background: "transparent" }} />

                <LabelText className="selected-zone-name">Squad PLayer</LabelText>
              </div>
              <div className="marker-container">
                <div className="square-marker" style={{ backgroundColor: "transparent" }}></div>
                <LabelText className="selected-zone-name">Fringe/Development playerr</LabelText>
              </div>
              {/* {selectedLabel === '1' && ( */}
              <div className="marker-container">
                <span className="league-square-marker" style={{ backgroundColor: "#00A1E4" }}></span>
                <LabelText className="selected-zone-name">League Average</LabelText>
              </div>
              {/* )} */}
            </div>
          </SquareChartContainer>
        </div>
      </div>
    </>
  );
};

export default SquareBarChart;
